import { useCallback, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Car,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Bell as BellIcon,
  HelpCircle,
  Settings as SettingsIcon,
  Shield,
} from '@tamagui/lucide-icons';
import { Paragraph, SizableText, useMedia, XStack, YStack } from 'tamagui';
import { useAuthStore } from '../lib/auth-store';
import { useMe } from '../lib/user-prefs';
import { NotificationsBell } from './NotificationsBell';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { useFocusRing } from '../lib/focus-ring';
import { useMenuKeyboard } from '../lib/use-menu-keyboard';
import { Z } from '../lib/z-index';

// Derive the icon component type from a lucide icon so we don't depend on
// `@tamagui/helpers-icon` being resolvable in this workspace.
type IconComponent = typeof LayoutDashboard;

interface NavDestination {
  to: string;
  label: string;
  icon: IconComponent;
  end?: boolean;
}

// Primary destinations live in the desktop top bar and the mobile bottom tabs.
const PRIMARY_NAV: NavDestination[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/vehicles', label: 'Vehicles', icon: Car },
  { to: '/notifications', label: 'Notifications', icon: BellIcon },
];

const TAB_BAR_HEIGHT = 60;
const TAP_TARGET = 44;

export function AppShell() {
  const media = useMedia();
  const { me } = useMe();
  const isAdmin = me?.role === 'ADMIN';

  return (
    <YStack flex={1} minHeight="100vh" backgroundColor="$background">
      {media.gtSm ? <DesktopTopBar isAdmin={isAdmin} /> : <MobileTopBar isAdmin={isAdmin} />}

      <YStack
        flex={1}
        padding="$4"
        // Keep page content clear of the fixed mobile tab bar.
        paddingBottom={media.gtSm ? '$4' : TAB_BAR_HEIGHT + 16}
      >
        <Outlet />
      </YStack>

      {media.gtSm ? null : <MobileTabBar />}
    </YStack>
  );
}

/* ------------------------------- Desktop ------------------------------- */

function DesktopTopBar({ isAdmin }: { isAdmin: boolean }) {
  return (
    <XStack
      paddingHorizontal="$4"
      paddingVertical="$3"
      borderBottomWidth={1}
      borderBottomColor="$borderColor"
      alignItems="center"
      justifyContent="space-between"
    >
      <XStack gap="$5" alignItems="center">
        <NavLink to="/" aria-label="CarTrack Pro home" style={NAVLINK_FLEX}>
          <SizableText color="$color12" display="flex">
            <Logo variant="full" height={22} />
          </SizableText>
        </NavLink>
        <XStack gap="$2" alignItems="center">
          {PRIMARY_NAV.map((item) => (
            <TopBarLink key={item.to} item={item} />
          ))}
        </XStack>
      </XStack>

      <XStack gap="$2" alignItems="center">
        <NotificationsBell />
        <AccountMenu isAdmin={isAdmin} />
      </XStack>
    </XStack>
  );
}

function TopBarLink({ item }: { item: NavDestination }) {
  return (
    <NavLink to={item.to} end={item.end} className="focus-ring" style={NAVLINK_FLEX}>
      {({ isActive }) => (
        <XStack
          alignItems="center"
          height={TAP_TARGET}
          paddingHorizontal="$3"
          borderRadius="$3"
          backgroundColor={isActive ? '$backgroundHover' : 'transparent'}
          hoverStyle={{ backgroundColor: '$backgroundHover' }}
        >
          <SizableText
            size="$3"
            color={isActive ? '$accent' : '$color10'}
            fontWeight={isActive ? '600' : '400'}
          >
            {item.label}
          </SizableText>
        </XStack>
      )}
    </NavLink>
  );
}

function AccountMenu({ isAdmin }: { isAdmin: boolean }) {
  const navigate = useNavigate();
  const { session, signOut } = useAuthStore();
  const [open, setOpen] = useState(false);
  const email = session?.user.email ?? '';
  const triggerRing = useFocusRing();

  const close = useCallback(() => setOpen(false), []);
  useMenuKeyboard(open, close, 'Account menu');
  const handleSignOut = async () => {
    close();
    await signOut();
    navigate('/login');
  };

  return (
    <YStack position="relative">
      <XStack
        tag="button"
        alignItems="center"
        gap="$2"
        height={TAP_TARGET}
        paddingHorizontal="$3"
        borderRadius="$3"
        backgroundColor="transparent"
        borderWidth={0}
        cursor="pointer"
        hoverStyle={{ backgroundColor: '$backgroundHover' }}
        pressStyle={{ backgroundColor: '$backgroundPress' }}
        onPress={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        {...triggerRing}
      >
        <Paragraph size="$2" color="$color10" maxWidth={180} numberOfLines={1}>
          {email}
        </Paragraph>
        <ChevronDown size={16} color="$color9" />
      </XStack>

      {open ? (
        <>
          <Backdrop onPress={close} />
          <YStack
            position="absolute"
            top={TAP_TARGET + 6}
            right={0}
            minWidth={200}
            zIndex={Z.dropdown}
            backgroundColor="$color3"
            borderWidth={1}
            borderColor="$borderColor"
            borderRadius="$4"
            paddingVertical="$2"
            elevation="$4"
            role="menu"
          >
            <MenuItem icon={SettingsIcon} label="Settings" onPress={() => { close(); navigate('/settings'); }} />
            <MenuItem icon={HelpCircle} label="Help & contact" onPress={() => { close(); navigate('/help'); }} />
            {isAdmin ? (
              <MenuItem icon={Shield} label="Admin" onPress={() => { close(); navigate('/admin'); }} />
            ) : null}
            <YStack height={1} backgroundColor="$borderColor" marginVertical="$2" />
            <ThemeToggle variant="menu" />
            <YStack height={1} backgroundColor="$borderColor" marginVertical="$2" />
            <MenuItem icon={LogOut} label="Sign out" onPress={handleSignOut} danger />
          </YStack>
        </>
      ) : null}
    </YStack>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onPress,
  danger,
}: {
  icon: IconComponent;
  label: string;
  onPress: () => void;
  danger?: boolean;
}) {
  const ring = useFocusRing();
  return (
    <XStack
      tag="button"
      role="menuitem"
      alignItems="center"
      gap="$3"
      width="100%"
      height={TAP_TARGET}
      paddingHorizontal="$3"
      backgroundColor="transparent"
      borderWidth={0}
      cursor="pointer"
      hoverStyle={{ backgroundColor: '$backgroundHover' }}
      pressStyle={{ backgroundColor: '$backgroundPress' }}
      onPress={onPress}
      {...ring}
    >
      <Icon size={18} color={danger ? '$danger' : '$color10'} />
      <SizableText size="$3" color={danger ? '$danger' : '$color11'}>
        {label}
      </SizableText>
    </XStack>
  );
}

/* -------------------------------- Mobile ------------------------------- */

function MobileTopBar({ isAdmin }: { isAdmin: boolean }) {
  return (
    <XStack
      paddingHorizontal="$3"
      paddingVertical="$2"
      borderBottomWidth={1}
      borderBottomColor="$borderColor"
      alignItems="center"
      justifyContent="space-between"
    >
      <NavLink to="/" aria-label="CarTrack Pro home" style={NAVLINK_FLEX}>
        <SizableText color="$color12" display="flex">
          <Logo variant="full" height={20} />
        </SizableText>
      </NavLink>
      <AccountMenu isAdmin={isAdmin} />
    </XStack>
  );
}

function MobileTabBar() {
  return (
    <XStack
      // CSS `position: fixed` pins the bar to the viewport while the page
      // scrolls. Tamagui's prop union omits "fixed", so it goes through the
      // web `style` escape hatch (viewport pinning, not a layout object).
      style={FIXED_BOTTOM}
      height={TAB_BAR_HEIGHT}
      zIndex={Z.tabBar}
      backgroundColor="$color2"
      borderTopWidth={1}
      borderTopColor="$borderColor"
      alignItems="stretch"
      justifyContent="space-around"
    >
      {PRIMARY_NAV.map((item) => (
        <TabBarLink key={item.to} item={item} />
      ))}
    </XStack>
  );
}

function TabBarLink({ item }: { item: NavDestination }) {
  const location = useLocation();
  // NotificationsBell carries its own badge; the tab is a plain icon+label.
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className="focus-ring"
      style={{ ...NAVLINK_RESET, flex: 1, display: 'flex' }}
    >
      {({ isActive }) => {
        const Icon = item.icon;
        // `isActive` for the index route can be stale during transitions; trust it.
        void location;
        return (
          <YStack
            flex={1}
            minHeight={TAP_TARGET}
            alignItems="center"
            justifyContent="center"
            gap="$1"
            paddingVertical="$2"
          >
            <Icon size={22} color={isActive ? '$accent' : '$color9'} />
            <SizableText
              size="$1"
              color={isActive ? '$accent' : '$color9'}
              fontWeight={isActive ? '600' : '400'}
            >
              {item.label}
            </SizableText>
          </YStack>
        );
      }}
    </NavLink>
  );
}

/* ------------------------------- Shared -------------------------------- */

function Backdrop({ onPress }: { onPress: () => void }) {
  // Full-viewport invisible layer so an outside click closes the menu.
  return <YStack style={FIXED_FILL} zIndex={Z.backdrop} onPress={onPress} />;
}

// react-router's NavLink renders an <a>; strip the UA link styling so the
// Tamagui children own all visuals. This is link reset only, not layout.
const NAVLINK_RESET: React.CSSProperties = {
  textDecoration: 'none',
  color: 'inherit',
};

// Logo link: lay out as a flex box and inherit color from the Tamagui wrapper
// (which sets a theme token) so the mark/wordmark recolor in light mode.
const NAVLINK_FLEX: React.CSSProperties = {
  display: 'flex',
  textDecoration: 'none',
  color: 'inherit',
};

// Viewport-pinning escape hatches (CSS `position: fixed` is outside Tamagui's
// position union). Pinning only — no flex/padding/margin layout here.
const FIXED_BOTTOM: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
};

const FIXED_FILL: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
};
